/**
 * StickyMagazineTabs
 * ===================
 *
 * Système d'onglets pédagogiques "style magazine" pour les hubs majeurs
 * de Code 2 Roues (espace élève connecté, espace enseignant, etc.).
 *
 * Caractéristiques :
 *  - Sticky en haut sous le header global (top: 56px par défaut).
 *  - Indicateur de section actif animé (barre rouge sous l'onglet sélectionné).
 *  - Deep-link via querystring `?tab=slug`.
 *  - Mobile : scrollable horizontalement si trop d'onglets.
 *  - Mémorise le dernier onglet dans le sessionStorage (clé personnalisable).
 *  - Accessibilité ARIA : rôles tablist / tab / tabpanel.
 *
 * Usage :
 *   <StickyMagazineTabs
 *     storageKey="teacher-hub"
 *     tabs={[
 *       { id: 'examen', label: 'Mon examen CCS', icon: GraduationCap, content: <ExamenContent /> },
 *       { id: 'cours',  label: 'Mes cours',      icon: BookOpen,      content: <CoursContent /> },
 *       { id: 'stats',  label: 'Mes stats',      icon: BarChart3,     content: <StatsContent /> },
 *     ]}
 *   />
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * @param {Object[]} tabs - liste des onglets
 * @param {string}   tabs[].id        - slug url-safe (ex: 'examen', 'cours')
 * @param {string}   tabs[].label     - libellé affiché
 * @param {React.Component} tabs[].icon - icône lucide-react (optionnel)
 * @param {React.Node} tabs[].content - contenu du panneau
 * @param {string}   tabs[].kicker    - sur-titre court optionnel (ex: '6 outils')
 * @param {string}   storageKey       - clé sessionStorage pour mémoriser le tab actif
 * @param {string}   stickyTop        - offset top CSS pour le sticky (default '56px')
 * @param {string}   className        - classes additionnelles
 */
export default function StickyMagazineTabs({
  tabs = [],
  storageKey = 'magazine-tabs',
  stickyTop = '0px',
  className = '',
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');

  // Tab initial : URL ?tab=… > sessionStorage > premier onglet
  const getInitial = () => {
    const valid = tabs.find((t) => t.id === tabFromUrl);
    if (valid) return valid.id;
    if (typeof window !== 'undefined') {
      try {
        const stored = window.sessionStorage.getItem(`c2r:${storageKey}`);
        if (stored && tabs.find((t) => t.id === stored)) return stored;
      } catch { /* noop */ }
    }
    return tabs[0]?.id || '';
  };

  const [active, setActive] = useState(getInitial);
  const tabsRef = useRef(null);
  const buttonRefs = useRef({});

  // Synchro URL ↔ état
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== active && tabs.find((t) => t.id === tabFromUrl)) {
      setActive(tabFromUrl);
    }
  }, [tabFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectTab = useCallback(
    (id) => {
      setActive(id);
      try { window.sessionStorage.setItem(`c2r:${storageKey}`, id); } catch { /* noop */ }
      const next = new URLSearchParams(searchParams);
      next.set('tab', id);
      setSearchParams(next, { replace: true });
      // Scroller l'onglet actif au centre de la barre sur mobile
      const btn = buttonRefs.current[id];
      if (btn && btn.scrollIntoView) {
        btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    },
    [searchParams, setSearchParams, storageKey],
  );

  const onKey = (e, idx) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      selectTab(tabs[(idx + 1) % tabs.length].id);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      selectTab(tabs[(idx - 1 + tabs.length) % tabs.length].id);
    } else if (e.key === 'Home') {
      e.preventDefault();
      selectTab(tabs[0].id);
    } else if (e.key === 'End') {
      e.preventDefault();
      selectTab(tabs[tabs.length - 1].id);
    }
  };

  const activeTab = tabs.find((t) => t.id === active) || tabs[0];

  return (
    <div className={className} data-testid="sticky-magazine-tabs">
      {/* BARRE D'ONGLETS — sticky, style magazine */}
      <div
        ref={tabsRef}
        className="sticky z-20 -mx-4 sm:mx-0 mb-6 sm:mb-8 border-b border-border bg-background/85 backdrop-blur-md"
        style={{ top: stickyTop }}
      >
        <div
          role="tablist"
          aria-label="Sections"
          className="flex items-stretch gap-0 overflow-x-auto px-4 sm:px-0 scrollbar-hide"
          style={{ scrollbarWidth: 'none' }}
        >
          {tabs.map((t, idx) => {
            const isActive = t.id === active;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                ref={(el) => { buttonRefs.current[t.id] = el; }}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`panel-${t.id}`}
                id={`tab-${t.id}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => selectTab(t.id)}
                onKeyDown={(e) => onKey(e, idx)}
                data-testid={`tab-${t.id}`}
                className={`relative flex-shrink-0 group flex items-center gap-2 px-4 sm:px-5 py-3 sm:py-4 text-sm font-bold tracking-tight transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] focus-visible:ring-inset
                  ${isActive
                    ? 'text-[hsl(var(--primary))]'
                    : 'text-muted-foreground hover:text-foreground'}`}
              >
                {Icon && (
                  <Icon
                    className={`w-4 h-4 sm:w-[18px] sm:h-[18px] flex-shrink-0 transition-transform ${
                      isActive ? 'scale-110' : 'group-hover:scale-105'
                    }`}
                  />
                )}
                <span className="flex flex-col items-start leading-none">
                  {t.kicker && (
                    <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.16em] opacity-70 mb-1">
                      {t.kicker}
                    </span>
                  )}
                  <span className="whitespace-nowrap">{t.label}</span>
                </span>
                {/* Indicateur actif animé */}
                <span
                  aria-hidden="true"
                  className={`absolute left-2 right-2 -bottom-px h-[3px] rounded-full transition-all duration-300 ease-out ${
                    isActive
                      ? 'bg-[hsl(var(--primary))] opacity-100 scale-x-100'
                      : 'bg-transparent opacity-0 scale-x-0'
                  }`}
                  style={{ transformOrigin: 'center' }}
                />
              </button>
            );
          })}
        </div>
      </div>

      {/* PANNEAU ACTIF */}
      {tabs.map((t) => (
        <div
          key={t.id}
          role="tabpanel"
          id={`panel-${t.id}`}
          aria-labelledby={`tab-${t.id}`}
          hidden={t.id !== active}
          data-testid={`panel-${t.id}`}
          className={t.id === active ? 'animate-in fade-in duration-300' : ''}
        >
          {t.id === active && t.content}
        </div>
      ))}
    </div>
  );
}
